import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";

const termsAndConditions = `
Terms and Conditions

Last Updated and Effective Date: April 15, 2025

Welcome to Clou ("Website" or "Platform"), owned and operated by The Clou Partnership ("Company," "we," "our," or "us"). By accessing or using Clou, you agree to be bound by these Terms of Use ("Terms"). If you do not agree with these Terms, please do not use the Website.

1. Overview of Clou
Clou is a platform that connects clients ("Clients") with independent therapists ("Therapists"). We do not provide therapy services ourselves, and all therapy sessions and advice are the sole responsibility of the participating Therapists.

Clients may also use Clou to keep personal journals and recordings. While we store these entries, we do not guarantee their preservation. In the event of a server malfunction, natural disaster, cyberattack, or any other unforeseen issue, we are not responsible for any loss, corruption, or deletion of journal or recording entries. Clients should always maintain their own backups of any important text, audio, or recordings.

2. Acceptance of Terms
By using Clou, you confirm that you are at least 18 years old and legally capable of entering into a binding agreement. If you use the Website on behalf of an entity, you represent that you have the authority to bind that entity to these Terms.

3. No Therapist-Client Relationship
Clou facilitates connections between Therapists and Clients but does not provide, control, or supervise any therapy services. Your engagement with any Therapist through the Website is solely between you and that Therapist. We do not endorse, warrant, or guarantee the qualifications, effectiveness, or suitability of any Therapist.
While we ask Therapists for their credentials, we cannot be held responsible for any fraudulent activities by the Therapists. Users are responsible for conducting their own due diligence before engaging with a Therapist.
Additionally, we are not responsible for any bios or profile information listed by any Client, Therapist, or other user. The accuracy, completeness, and truthfulness of all user-provided content are the sole responsibility of the individual users.

4. GDPR Compliance & Data Privacy
Clou is committed to complying with the General Data Protection Regulation (GDPR) and ensuring that user data is handled securely and transparently.

Users have the right to request access to, correction of, or deletion of their personal data.
Clou does not sell or share personal data with third parties without user consent, except as required by law.
While we take reasonable measures to secure user data, we do not guarantee absolute security and are not liable for any data breaches or unauthorised access beyond our reasonable control.
Users should not store highly sensitive personal data (e.g., medical records) on the platform, as we are not a regulated healthcare provider.
For full details on how we collect, use, and store personal data, please review our Privacy Policy.

5. No Liability for Emotional or Psychological Harm
Clou is not responsible for any emotional distress, psychological harm, or negative experiences arising from:

Therapist interactions
Journal reflections
Platform-based advice

6. No Guarantee of Service Availability
We do not guarantee uninterrupted, error-free, or secure operation of Clou. The Website may experience:

Downtime due to maintenance or technical failures
Service disruptions from cyberattacks, software bugs, or third-party malfunctions
Delays or errors beyond our control

7. No Endorsement or Verification of User Content
We do not endorse, verify, or take responsibility for any content uploaded by users, including therapist bios, client reviews, or journal entries. All user-generated content is the sole responsibility of the individual user.

8. No Responsibility for Misuse of the Platform
Users agree that they will not:

Use the platform for unlawful, misleading, or fraudulent purposes.
Harass, threaten, or impersonate any other user.
Post or share false, harmful, or misleading content.
Clou reserves the right to suspend or terminate any account found in violation of these terms.

9. No Guarantee of Outcomes
Clou makes no guarantees that using the platform will result in mental health improvements or personal well-being. Journaling and therapy interactions are personal experiences, and results may vary.

10. No AI or Automated Decision Liability
If Clou offers AI-generated insights, recommendations, or responses, users agree that:

AI-generated content is for informational purposes only.
AI responses should not be considered medical or professional advice.
Clou is not responsible for any decisions made based on AI-generated content.

11. Force Majeure Clause
Clou shall not be liable for any failure or delay in providing services due to circumstances beyond our control, including but not limited to:

Natural disasters
Cyberattacks
Government actions
Technical failures

12. Arbitration Clause
To prevent legal disputes, any disagreements or claims must be resolved through arbitration instead of lawsuits. This means:

No user may file a lawsuit against Clou.
Disputes must be handled privately through an independent arbitrator.
Arbitration takes place in [Jurisdiction], and the arbitrator's decision is final.

13. Indemnification Clause
You agree to indemnify, defend, and hold harmless Clou, its owners, employees, and affiliates from any claims, liabilities, or damages resulting from:

Your use of the Website
Your interactions with Therapists
Any breach of these Terms

14. Limitation of Liability
To the fullest extent permitted by law, Clou, its owners, employees, and affiliates shall not be liable for:

Any direct, indirect, incidental, consequential, or punitive damages
Any loss, corruption, or deletion of journal or recording entries
Any unauthorised access to our servers or data
Any fraudulent activities by Therapists or misinformation posted by any user

15. Modifications to Terms
We reserve the right to modify these Terms at any time. Any changes will be posted on this page, and continued use of the Website after modifications constitutes acceptance of the updated Terms.

16. Termination
We may suspend or terminate your access to Clou at any time for any reason, including violations of these Terms.

17. Governing Law
These Terms shall be governed by and construed in accordance with the laws of Portugal. Any disputes arising from these Terms shall be resolved exclusively in the courts of Portugal.

18. Contact Information
For questions, email us at hello@clamalo.com.
`;

interface TermsAndConditionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  userType: "client" | "therapist";
  isSubmitting: boolean;
}

const TermsAndConditionsDialog: React.FC<TermsAndConditionsDialogProps> = ({
  open,
  onOpenChange,
  onAccept,
  userType,
  isSubmitting,
}) => {
  const handleAccept = () => {
    onAccept();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Terms and Conditions</DialogTitle>
          <DialogDescription>
            Please review our terms and conditions
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] mt-4 border rounded-md p-4 text-sm">
          <div className="whitespace-pre-line">{termsAndConditions}</div>
        </ScrollArea>
        <DialogFooter className="flex justify-between items-center gap-4 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Close
          </Button>
          <Button
            onClick={handleAccept}
            className="flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Accept Terms"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsAndConditionsDialog; 